import { z } from 'zod';
import { Role } from './constants.js';

export const LoginSchema = z.object({
  usernameOrEmail: z.string().min(3).max(100),
  password: z.string().min(6).max(128),
});

export type LoginInput = z.infer<typeof LoginSchema>;

export const CreateUserSchema = z.object({
  username: z.string().min(3).max(32).regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores and dashes'),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  role: z.nativeEnum(Role).default(Role.VIEWER),
});

export type CreateUserInput = z.infer<typeof CreateUserSchema>;

export const UpdateUserSchema = z.object({
  username: z.string().min(3).max(32).optional(),
  email: z.string().email().optional(),
  password: z.string().min(8).max(128).optional(),
  role: z.nativeEnum(Role).optional(),
});

export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

export const CreateServerSchema = z.object({
  name: z.string().min(2).max(64),
  description: z.string().max(256).optional(),
  host: z.string().default('0.0.0.0'),
  port: z.number().int().min(1024).max(65535).default(7777),
  queryPort: z.number().int().min(1024).max(65535).default(1404).optional(),
  domain: z.string().max(128).optional(),
  maxPlayers: z.number().int().min(1).max(512).default(32),
  gamemode: z.string().min(1).max(64).default('freeroam.lua'),
  level: z.enum(['klaster', 'trosecko', 'kutnohorsko']).default('klaster').optional(),
  map: z.string().min(1).max(64).default('klaster'),
  password: z.string().max(64).optional(),
  autoRestart: z.boolean().default(true),
  installTemplate: z.string().optional(),
  databaseDriver: z.string().optional(),
  databaseHost: z.string().optional(),
  databasePort: z.number().int().optional(),
  databaseUser: z.string().optional(),
  databasePassword: z.string().optional(),
  databaseName: z.string().optional(),
});

export type CreateServerInput = z.infer<typeof CreateServerSchema>;

export const UpdateServerSchema = CreateServerSchema.partial();
export type UpdateServerInput = z.infer<typeof UpdateServerSchema>;

export const KcdDatabaseConfigSchema = z.object({
  type: z.enum(['mysql', 'postgres', 'sqlite']),
  host: z.string().optional(),
  port: z.number().int().min(1).max(65535).optional(),
  user: z.string().optional(),
  password: z.string().optional(),
  database: z.string().optional(),
  filename: z.string().optional(),
});

export const KcdServerTomlConfigSchema = z.object({
  name: z.string().min(1).max(100),
  motd: z.string().max(500).optional().default(''),
  host: z.string().default('0.0.0.0'),
  port: z.number().int().min(1024).max(65535).default(7778),
  query_port: z.number().int().min(1024).max(65535).optional(),
  max_players: z.number().int().min(1).max(512).default(32),
  password: z.string().optional().default(''),
  gamemode: z.string().min(1).default('xoamRP'),
  map: z.string().min(1).default('Kuttenberg'),
  level: z.string().optional(),
  announce: z.boolean().default(false),
  master_server: z.string().optional(),
  token: z.string().optional(),
  watch_mode: z.boolean().default(false),
  log_level: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  database: KcdDatabaseConfigSchema.optional(),
  custom: z.record(z.unknown()).optional(),
});

export type KcdServerTomlConfigInput = z.infer<typeof KcdServerTomlConfigSchema>;

export const FileWriteSchema = z.object({
  path: z.string().min(1),
  content: z.string(),
});

export const FileRenameSchema = z.object({
  oldPath: z.string().min(1),
  newPath: z.string().min(1),
});

export const FileDeleteSchema = z.object({
  paths: z.array(z.string().min(1)),
});

export const FileCreateSchema = z.object({
  path: z.string().min(1),
  isDirectory: z.boolean().default(false),
});

export const FileZipSchema = z.object({
  targetZipPath: z.string().min(1),
  sourcePaths: z.array(z.string().min(1)),
});

export const FileUnzipSchema = z.object({
  zipPath: z.string().min(1),
  destinationPath: z.string().min(1),
});

export const ConsoleCommandSchema = z.object({
  command: z.string().min(1).max(512),
});

export const PlayerActionSchema = z.object({
  action: z.enum(['kick', 'ban', 'message', 'teleport']),
  reason: z.string().max(256).optional(),
  message: z.string().max(512).optional(),
  durationMinutes: z.number().int().min(0).optional(),
  destination: z.object({ x: z.number(), y: z.number(), z: z.number() }).optional(),
});

export const DatabaseQuerySchema = z.object({
  query: z.string().min(1).max(4000),
  limit: z.number().int().min(1).max(1000).default(100),
});

export const CreateDatabaseSchema = z.object({
  name: z.string().min(2).max(32).regex(/^[a-zA-Z0-9_]+$/, 'Database name can only contain letters, numbers, and underscores'),
  type: z.enum(['mysql', 'mariadb']).default('mysql'),
});

export const CreateApiKeySchema = z.object({
  name: z.string().min(3).max(64),
  expiresInDays: z.number().int().min(1).max(365).optional(),
});
